import {
  MagnifyingGlassIcon,
  ChevronUpDownIcon,
} from "@heroicons/react/24/outline";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import {
  PencilIcon,
  ArrowDownIcon,
  UserPlusIcon,
} from "@heroicons/react/24/solid";
import {
  Dialog,
  Card,
  CardHeader,
  Input,
  Typography,
  Button,
  CardBody,
  Chip,
  CardFooter,
  IconButton,
  Tooltip,
  Select,
  Option,
} from "@material-tailwind/react";
import {
  getUserData,
  createUserManagementData,
  getClientRoleData,
  editUserData,
  deleteClientUser,
} from "../../helperFunctions/apiFunction";
import { useAuthUserToken } from "../../hooks/useAuthUserToken";
import TableSkeleton from "../../components/Loaders/TableSkeleton";
import { Delete } from "@mui/icons-material";
import LoadingButton from "../../components/Loaders/LoadingButton";
import ExcelJS from "exceljs";

const TABLE_HEAD = ["Name", "Email", "Role", "Status", "Actions"];

export default function UserManagement() {
  const { refreshToken, rbac } = useAuthUserToken();
  const [templateData, setTemplateData] = useState([]);
  const [roleData, setRoleData] = useState([]);
  const [isLoading, setIsLoading] = useState(null);
  const [searchBy, setSearchBy] = useState("");
  const [sortBy, setSortBy] = useState("asc");
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(1);
  const [errors, setErrors] = useState({ name: "" });
  const [reloadData, setReloadData] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isLoadingButton, setIsLoadingButton] = useState(false);
  const [open, setOpen] = useState(false);
  const [id, setId] = useState("");
  const [readyForSubmission, setReadyForSubmission] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const handleOpenCreateDialog = () => {
    setOpenCreateDialog((cur) => !cur);
  };

  const initialEditData = {
    fullName: "",
    userEmail: "",
    roleId: "",
    userStatus: "",
  };
  const [editFormData, setEditFormData] = useState(initialEditData);

  const handleOpen = (item) => {
    setEditFormData({
      fullName: item?.fullName || "",
      userEmail: item?.userEmail || "",
      roleId: item?.roleId || "",
      userStatus: item?.status || "",
    });
    setId(item?.id || "");
    setErrors({ name: "" });
    setOpen((cur) => !cur);
  };

  const validateEditForm = () => {
    const newErrors = {};
    if (!editFormData?.roleId?.trim()) newErrors.roleId = "Role is required";
    if (!editFormData.userStatus.trim())
      newErrors.userStatus = "Status is required";
    setErrors(newErrors);
  };

  useEffect(() => {
    getUserData(refreshToken, searchBy, sortBy, limit, page, status).then(
      (data) => {
        if (data?.code === 440)
          toast.error(data?.message, {
            className: "text-[12px]",
          });
        setTotalPage(data?.data?.totalPages);
        setTemplateData(data?.data?.results);
        setIsLoading(false);
      }
    );
    if (roleData.length === 0) {
      getClientRoleData(refreshToken, "", "asc", 100, 1, "").then((resp) => {
        if (resp?.code === 440)
          toast.error(resp?.message, {
            className: "text-[12px]",
          });
        setRoleData(resp?.data?.results);
      });
    }
  }, [page, limit, sortBy, searchBy, status, reloadData]);

  const handleDelete = (id) => {
    deleteClientUser(refreshToken, id)
      .then((response) => {
        if (response?.code === 200 || response?.code === 201) {
          toast.success("User deleted successfully", {
            className: "text-[12px]",
          });
        } else if (response.code === 400) {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        } else {
          toast.error(response?.message, {
            className: "text-[12px]",
          });
        }
      })
      .catch((error) => {
        toast.error("Something went wrong!", {
          className: "text-[12px]",
        });
        console.error("Error during login:", error);
      })
      .finally(() => {
        setIsLoading(null);
        setIsLoadingButton(false);
        setId("");
        setDeleteDialog((cur) => !cur);
        setReloadData((cur) => !cur);
      });
  };

  const handleDeleteDialog = (id) => {
    setId(id);
    setDeleteDialog((cur) => !cur);
  };

  // Create Form Functionality
  const initialFormData = {
    fullName: "",
    userEmail: "",
    password: "",
    confirmPassword: "",
    roleId: "",
    userStatus: "",
  };

  const [formData, setFormData] = useState(initialFormData);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.userEmail.trim()) newErrors.userEmail = "Email is required";
    if (!formData.password.trim()) newErrors.password = "Password is required";
    if (!formData.confirmPassword.trim())
      newErrors.confirmPassword = "Confirm Password is required";
    if (formData.password.trim() !== formData.confirmPassword.trim())
      newErrors.confirmPassword = "Password & confirm password doesn't match";
    if (!formData.roleId.trim()) newErrors.roleId = "Role is required";
    if (!formData.userStatus.trim())
      newErrors.userStatus = "Status is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleBlur = () => {
    const newErrors = {};
    if (!editFormData.fullName.trim())
      newErrors.fullName = "Full name is required";
    if (!editFormData.userEmail.trim())
      newErrors.userEmail = "Email is required";
    setErrors(newErrors);
  };

  const handleSubmitBlur = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
    setEditFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleChangeEditSelect = (e, fieldName) => {
    setErrors((prevErrors) => ({ ...prevErrors, [fieldName]: "" }));
    setEditFormData((prevData) => ({
      ...prevData,
      [fieldName]: e,
    }));
  };

  const handleEditBlur = () => {
    validateEditForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoadingButton(true);
    if (validateForm()) {
      setReadyForSubmission(true);
    } else {
      setIsLoadingButton(false);
    }
  };

  useEffect(() => {
    if (readyForSubmission) {
      createUserManagementData(refreshToken, formData)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("User created successfully", {
              className: "text-[12px]",
            });
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setFormData(initialFormData);
          setErrors({ name: "" });
          setIsLoadingButton(false);
          setReadyForSubmission(false);
          setOpenCreateDialog((cur) => !cur);
          setReloadData((cur) => !cur);
        });
    }
  }, [readyForSubmission]);
  // End Create Form Functionality

  // Edit form functionality
  const handleEdit = async (e) => {
    e.preventDefault();
    validateEditForm();
    if (Object.keys(errors).length === 0) {
      setIsLoadingButton(true);
      editUserData(refreshToken, editFormData, id)
        .then((response) => {
          if (response?.code === 200 || response?.code === 201) {
            toast.success("User Data updated successfully", {
              className: "text-[12px]",
            });
          } else if (response.code === 400) {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          } else {
            toast.error(response?.message, {
              className: "text-[12px]",
            });
          }
        })
        .catch((error) => {
          toast.error("Something went wrong!", {
            className: "text-[12px]",
          });
          console.error("Error during login:", error);
        })
        .finally(() => {
          setEditFormData(initialEditData);
          setIsLoadingButton(false);
          setOpen((cur) => !cur);
          setReloadData((cur) => !cur);
        });
    }
  };

  const exportToExcel = () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Users Data");
    const headers = ["S.No.", "Name", "Contact Email", "Role"];
    worksheet.addRow(headers);
    templateData.forEach((row, index) => {
      worksheet.addRow([index + 1, row.fullName, row.userEmail, row.role]);
    });
    workbook.xlsx.writeBuffer().then((data) => {
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "users_data.xlsx";
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <div className="app-main__inner !pl-20 h-lvh">
      <Card
        className="h-full w-full rounded-3xl shadow-xl"
        style={{ backgroundImage: `url('/assets/images/beams.jpg')` }}
      >
        <CardHeader
          floated={false}
          shadow={false}
          className=" bg-transparent rounded-none !overflow-visible"
        >
          <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between md:gap-8">
            <div>
              <Typography variant="h5" color="blue-gray">
                List of Users
              </Typography>
              <Typography color="gray" className="mt-1 font-normal">
                View all users onboarded to CSPM
              </Typography>
            </div>
            <div className="flex flex-row md:flex-row items-center justify-between md:items-center gap-2 md:gap-8 mt-4 md:mt-0">
              <Button
                variant="outlined"
                size="sm"
                onClick={exportToExcel}
                loading={isLoading === null ? true : false}
              >
                <ArrowDownIcon strokeWidth={2} className="h-4 w-4" />
              </Button>
              {rbac?.userAdd &&
                <Button
                  onClick={handleOpenCreateDialog}
                  className="flex items-center gap-3"
                  loading={isLoading === null ? true : false}
                  size="sm"
                >
                  <UserPlusIcon strokeWidth={2} className="h-4 w-4" />{" "}
                  <span className="hidden md:inline-block">Create New</span>
                </Button>}
            </div>
          </div>
          <div className="flex flex-col items-center justify-between gap-6 md:flex-row">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 w-full">
              <Select
                color="blue"
                className="!border-t-0 !border-[#b0bec5]"
                label="Select Limit"
                onChange={(event) => {
                  setLimit(event);
                  setIsLoading(null);
                }}
                value={limit}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={50}>50</Option>
              </Select>
              <Select
                color="purple"
                className="!border-t-0 !border-[#b0bec5]"
                label="Sort By"
                value={sortBy}
                onChange={(event) => {
                  setSortBy(event);
                  setIsLoading(null);
                }}
              >
                <Option value="asc">Ascending</Option>
                <Option value="desc">Descending</Option>
              </Select>
              <Select
                color="green"
                className="!border-t-0 !border-[#b0bec5]"
                label="Status"
                value={status}
                onChange={(event) => {
                  setStatus(event);
                  setIsLoading(null);
                }}
              >
                <Option value="">All Data</Option>
                <Option value="active">Active</Option>
                <Option value="inactive">Inactive</Option>
              </Select>
            </div>
            <div className="w-full md:w-auto">
              <Input
                label="Search"
                className="focus:!border-black focus:!border-2 focus:!border-t-0"
                value={searchBy}
                onChange={(e) => setSearchBy(e.target.value)}
                icon={<MagnifyingGlassIcon className="h-5 w-5" />}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="overflow-scroll px-3 p-0">
          <table className="mt-4 w-full min-w-max table-auto text-left">
            <thead>
              <tr>
                {TABLE_HEAD.map((head, index) => (
                  <th
                    key={head}
                    className="cursor-pointer border-y border-blue-gray-100 bg-blue-gray-50/50 p-3 transition-colors hover:bg-blue-gray-50"
                  >
                    <Typography
                      variant="small"
                      color="blue-gray"
                      className="flex items-center justify-between gap-2 font-bold leading-none opacity-70"
                    >
                      {head}{" "}
                      {index !== TABLE_HEAD.length - 1 && (
                        <ChevronUpDownIcon
                          strokeWidth={2}
                          className="h-4 w-4"
                        />
                      )}
                    </Typography>
                  </th>
                ))}
              </tr>
            </thead>
            {isLoading === null ? (
              <>
                <tbody>
                  <tr>
                    <td colSpan={TABLE_HEAD.length}>
                      <TableSkeleton />
                    </td>
                  </tr>
                </tbody>
              </>
            ) : (
              <>
                <tbody>
                  {isLoading === false && templateData?.length > 0 ? (
                    templateData.map(
                      (
                        { fullName, userEmail, userStatus, roleId, id },
                        index
                      ) => {
                        const isLast = index === templateData.length - 1;
                        const classes = isLast
                          ? "p-3"
                          : "p-3 border-b border-blue-gray-50";

                        return (
                          <tr key={`${id}-${index}`}>
                            <td className={classes}>
                              <div className="flex items-center gap-3">
                                <Typography
                                  variant="small"
                                  color="blue-gray"
                                  className="font-normal"
                                >
                                  {fullName}
                                </Typography>
                              </div>
                            </td>

                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {userEmail}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <Typography
                                variant="small"
                                color="blue-gray"
                                className="font-normal"
                              >
                                {roleId?.roleName}
                              </Typography>
                            </td>
                            <td className={classes}>
                              <div className="w-max">
                                <Chip
                                  variant="ghost"
                                  size="sm"
                                  value={
                                    userStatus === "active"
                                      ? "Active"
                                      : "Inactive"
                                  }
                                  color={
                                    userStatus === "inactive" ? "red" : "green"
                                  }
                                />
                              </div>
                            </td>
                            <td className={classes}>
                              <div className="flex gap-1.5">
                                {rbac?.userEdit || rbac?.userDel ? (
                                  <>
                                    {rbac?.userEdit && (
                                      <Tooltip
                                        className="bg-blue-gray-500"
                                        content="Edit"
                                      >
                                        <IconButton
                                          variant="text"
                                          onClick={() =>
                                            handleOpen({
                                              fullName,
                                              userEmail,
                                              status: userStatus,
                                              roleId: roleId?.id,
                                              id,
                                            })
                                          }
                                        >
                                          <PencilIcon className="h-4 w-4" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    {rbac?.userDel &&
                                      <Tooltip
                                        className="bg-blue-gray-500"
                                        content="Delete"
                                      >
                                        <IconButton
                                          variant="text"
                                          onClick={() => handleDeleteDialog(id)}
                                        >
                                          <Delete className="h-4 w-4" />
                                        </IconButton>
                                      </Tooltip>}
                                  </>
                                ) : (
                                  <td></td>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      }
                    )
                  ) : (
                    <tr>
                      <td colSpan="8">
                        <div className="flex flex-col items-center w-full">
                          <img
                            quality={100}
                            src="/assets/images/9276430.jpg"
                            className="w-[350px] mix-blend-darken"
                            alt="no-data-found"
                          />
                          <p className="text-[20px] font-medium">
                            No Data Found!
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </>
            )}
          </table>
        </CardBody>
        {isLoading === null ? null : (
          <>
            <CardFooter className="flex items-center justify-between border-t border-blue-gray-50 p-4">
              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page - 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Previous
              </Button>
              <div className="flex items-center gap-2">
                {Array.from({ length: totalPage }, (_, index) => (
                  <IconButton
                    key={index + 1}
                    variant={index + 1 === page ? "outlined" : "text"}
                    size="sm"
                    onClick={() => {
                      setPage(index + 1);
                      setIsLoading(null);
                    }}
                    disabled={totalPage > 1 ? false : true}
                  >
                    {index + 1}
                  </IconButton>
                ))
                  .reduce((acc, button, index, array) => {
                    if (
                      index < 3 ||
                      index >= totalPage - 3 ||
                      (index >= Math.max(page - 1, 3) &&
                        index <= Math.min(page + 1, totalPage - 4))
                    ) {
                      acc.push(button);
                    } else if (acc[acc.length - 1] !== "ellipsis") {
                      acc.push("ellipsis");
                    }
                    return acc;
                  }, [])
                  .map((element, index, array) => {
                    return element === "ellipsis" ? (
                      <IconButton
                        key={`ellipsis-${index}`}
                        variant="text"
                        size="sm"
                        disabled
                      >
                        ...
                      </IconButton>
                    ) : index > 0 && array[index - 1] === "ellipsis" ? (
                      <React.Fragment key={`fragment-${index}`}>
                        {element}
                      </React.Fragment>
                    ) : (
                      element
                    );
                  })}
              </div>

              <Button
                variant="outlined"
                size="sm"
                onClick={() => {
                  setPage(page + 1);
                  setIsLoading(null);
                }}
                disabled={totalPage > 1 ? false : true}
              >
                Next
              </Button>
            </CardFooter>
          </>
        )}
      </Card>

      {/* Edit User form */}
      <Dialog
        size="lg"
        open={open}
        handler={handleOpen}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleEdit}>
          <Card className="mx-auto w-full p-4">
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray">
                Edit | User Management
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Full Name
                  </Typography>
                  <Input
                    label="Full name"
                    size="lg"
                    name="fullName"
                    value={editFormData?.fullName}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                    disabled
                  />
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Email
                  </Typography>
                  <Input
                    label="User email"
                    size="lg"
                    name="userEmail"
                    value={editFormData.userEmail}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                    disabled
                  />
                </div>
                <div>
                  <Typography className="mb-0" variant="h6">
                    Status
                  </Typography>
                  <div className="flex gap-10">
                    <div className="inline-flex items-center">
                      <label
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                        htmlFor="custom-style1"
                      >
                        <input
                          name="userStatus"
                          value="active"
                          type="radio"
                          checked={editFormData.userStatus === "active"}
                          onChange={handleEditChange}
                          onBlur={handleEditBlur}
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-900/10 bg-gray-900/5 p-0 text-gray-900 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-900 checked:before:bg-gray-900 hover:before:opacity-0"
                          id="custom-style1"
                        />
                        <span className="absolute text-gray-900 transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-full h-full scale-105"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                              clip-rule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </label>
                      <label
                        className="mt-px font-light text-gray-700 cursor-pointer select-none"
                        htmlFor="custom-style1"
                      >
                        <p className="block font-sans text-base antialiased font-normal leading-relaxed text-blue-gray-400">
                          Active
                        </p>
                      </label>
                    </div>
                    <div className="inline-flex items-center">
                      <label
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                        htmlFor="custom-style2"
                      >
                        <input
                          type="radio"
                          name="userStatus"
                          value="inactive"
                          checked={editFormData.userStatus === "inactive"}
                          onChange={handleEditChange}
                          onBlur={handleEditBlur}
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-900/10 bg-gray-900/5 p-0 text-gray-900 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-900 checked:before:bg-gray-900 hover:before:opacity-0"
                          id="custom-style2"
                        />
                        <span className="absolute text-gray-900 transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-full h-full scale-105"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                              clip-rule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </label>
                      <label
                        className="mt-px font-light text-gray-700 cursor-pointer select-none"
                        htmlFor="custom-style2"
                      >
                        <p className="block font-sans text-base antialiased font-normal leading-relaxed text-blue-gray-400">
                          Inactive
                        </p>
                      </label>
                    </div>
                  </div>
                  {errors.userStatus && (
                    <p className="text-red-900 text-[12px]">
                      {errors.userStatus}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Select Role
                  </Typography>
                  <Select
                    label="Role"
                    color="purple"
                    onChange={(e) => handleChangeEditSelect(e, "roleId")}
                    onBlur={handleBlur}
                    name="roleId"
                    value={editFormData?.roleId || ""}
                  >
                    {roleData &&
                      roleData.length > 0 &&
                      roleData.map((resp) => (
                        <Option key={resp?.id} value={resp?.id}>
                          {resp?.roleName}
                        </Option>
                      ))}
                  </Select>
                  {errors.role && (
                    <p className="text-red-900 text-[12px]">{errors.role}</p>
                  )}
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpen}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button variant="gradient" color="green" type="submit">
                  <span>Update</span>
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>

      {/* Add new User */}
      <Dialog
        size="lg"
        open={openCreateDialog}
        handler={() => setOpenCreateDialog(false)}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <form onSubmit={handleSubmit}>
          <Card className="mx-auto w-full p-4">
            <CardBody className="flex flex-col gap-4">
              <Typography variant="h4" color="blue-gray">
                Add New | User Information
              </Typography>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Full Name
                  </Typography>
                  <Input
                    type="text"
                    label="Full name"
                    size="lg"
                    name="fullName"
                    onChange={handleChange}
                    onBlur={handleSubmitBlur}
                    // value={form?.integrationProvider}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.fullName && (
                    <p className="text-red-900 text-[12px]">
                      {errors.fullName}
                    </p>
                  )}
                </div>

                <div>
                  <Typography className="mb-1 " variant="h6">
                    User Email
                  </Typography>
                  <Input
                    type="email"
                    label="User Email"
                    size="lg"
                    name="userEmail"
                    onChange={handleChange}
                    onBlur={handleSubmitBlur}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.userEmail && (
                    <p className="text-red-900 text-[12px]">
                      {errors.userEmail}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Password
                  </Typography>
                  <Input
                    type="password"
                    label="Password"
                    size="lg"
                    name="password"
                    onChange={handleChange}
                    // value={selectedItem?.integrationTitle}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.password && (
                    <p className="text-red-900 text-[12px]">
                      {errors.password}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Confirm Password
                  </Typography>
                  <Input
                    type="password"
                    label="Confirm Password"
                    size="lg"
                    name="confirmPassword"
                    onChange={handleChange}
                    // value={selectedItem?.integrationTitle}
                    className="focus:!border-2 focus:!border-t-0 focus:!border-black"
                  />
                  {errors.confirmPassword && (
                    <p className="text-red-900 text-[12px]">
                      {errors.confirmPassword}
                    </p>
                  )}
                </div>
                <div>
                  <Typography className="mb-1 " variant="h6">
                    Select Role
                  </Typography>
                  <Select
                    label="Role"
                    color="purple"
                    name="roleId"
                    onChange={(e) => handleChangeSelect(e, "roleId")}
                  >
                    {roleData &&
                      roleData.length > 0 &&
                      roleData.map((resp) => (
                        <Option key={resp?.id} value={resp?.id}>
                          {resp?.roleName}
                        </Option>
                      ))}
                  </Select>
                  {errors.roleId && (
                    <p className="text-red-900 text-[12px]">{errors.roleId}</p>
                  )}
                </div>
                <div>
                  <Typography className="mb-0" variant="h6">
                    Status
                  </Typography>
                  <div className="flex gap-10">
                    <div className="inline-flex items-center">
                      <label
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                        htmlFor="custom-style1"
                      >
                        <input
                          name="userStatus"
                          value="active"
                          type="radio"
                          onChange={handleChange}
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-900/10 bg-gray-900/5 p-0 text-gray-900 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-900 checked:before:bg-gray-900 hover:before:opacity-0"
                          id="custom-style1"
                        />
                        <span className="absolute text-gray-900 transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-full h-full scale-105"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                              clip-rule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </label>
                      <label
                        className="mt-px font-light text-gray-700 cursor-pointer select-none"
                        htmlFor="custom-style1"
                      >
                        <p className="block font-sans text-base antialiased font-normal leading-relaxed text-blue-gray-400">
                          Active
                        </p>
                      </label>
                    </div>
                    <div className="inline-flex items-center">
                      <label
                        className="relative flex items-center p-3 rounded-full cursor-pointer"
                        htmlFor="custom-style2"
                      >
                        <input
                          type="radio"
                          name="userStatus"
                          value="inactive"
                          onChange={handleChange}
                          className="before:content[''] peer relative h-5 w-5 cursor-pointer appearance-none rounded-full border border-gray-900/10 bg-gray-900/5 p-0 text-gray-900 transition-all before:absolute before:top-2/4 before:left-2/4 before:block before:h-12 before:w-12 before:-translate-y-2/4 before:-translate-x-2/4 before:rounded-full before:bg-blue-gray-500 before:opacity-0 before:transition-opacity checked:border-gray-900 checked:before:bg-gray-900 hover:before:opacity-0"
                          id="custom-style2"
                        />
                        <span className="absolute text-gray-900 transition-opacity opacity-0 pointer-events-none top-2/4 left-2/4 -translate-y-2/4 -translate-x-2/4 peer-checked:opacity-100">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="currentColor"
                            className="w-full h-full scale-105"
                          >
                            <path
                              fill-rule="evenodd"
                              d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm13.36-1.814a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z"
                              clip-rule="evenodd"
                            ></path>
                          </svg>
                        </span>
                      </label>
                      <label
                        className="mt-px font-light text-gray-700 cursor-pointer select-none"
                        htmlFor="custom-style2"
                      >
                        <p className="block font-sans text-base antialiased font-normal leading-relaxed text-blue-gray-400">
                          Inactive
                        </p>
                      </label>
                    </div>
                  </div>
                  {errors.userStatus && (
                    <p className="text-red-900 text-[12px]">
                      {errors.userStatus}
                    </p>
                  )}
                </div>
              </div>
            </CardBody>
            <CardFooter className="flex justify-end border-t-2 py-2">
              <Button
                variant="text"
                color="red"
                onClick={handleOpenCreateDialog}
                className="mr-1"
              >
                <span>Cancel</span>
              </Button>
              {isLoadingButton ? (
                <>
                  <LoadingButton isSetText={true} isLoading={isLoading} />
                </>
              ) : (
                <Button variant="gradient" type="submit" color="green">
                  <span>Create</span>
                </Button>
              )}
            </CardFooter>
          </Card>
        </form>
      </Dialog>

      {/* Delete User data */}
      <Dialog
        size="sm"
        open={deleteDialog}
        handler={setDeleteDialog}
        animate={{
          mount: { scale: 1, y: 0 },
          unmount: { scale: 0.9, y: -100 },
        }}
      >
        <Card className="mx-auto w-full p-2">
          <CardBody className="flex flex-col gap-4">
            <Typography variant="h6 mb-1" color="blue-gray">
              Are you sure you want to delete?
            </Typography>
          </CardBody>
          <CardFooter className="flex justify-end py-2">
            <Button
              variant="text"
              color="red"
              onClick={() => {
                setId("");
                setDeleteDialog(false);
              }}
              className="mr-1"
            >
              <span>Cancel</span>
            </Button>
            {isLoadingButton ? (
              <>
                <LoadingButton isSetText={true} isLoading={isLoading} />
              </>
            ) : (
              <Button
                variant="gradient"
                type="submit"
                color="green"
                loading={isLoadingButton}
                onClick={() => handleDelete(id)}
              >
                <span>Delete</span>
              </Button>
            )}
          </CardFooter>
        </Card>
      </Dialog>
    </div>
  );
}
